import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api.ts";
import { User } from "@/javascript/user-objects.ts";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, Group, PasswordInput, Skeleton, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck, IconLock, IconUser } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useState } from "react";
import ConductionModal from "../ConductionModal";

interface StudentPasswordUpdateFormProps {
	userId: string;
	onClose: () => void;
}

const StudentPasswordUpdateForm: React.FC<StudentPasswordUpdateFormProps> = ({ userId, onClose }) => {
	const [subjectUser, setSubjectUser] = useState<User | null>(null);
	const [loading, setLoading] = useState<boolean>(true);

	const auth = useContext(AuthContext);
	if (auth === undefined) {
		throw new Error("ProjectSelect must be a child of AuthContext.");
	}
	const user = auth.user;
	if (user === null) {
		return;
	}
	const token = auth.token;
	if (token === null) {
		return;
	}

	const form = useForm({
		initialValues: {
			newPassword: "",
		},
		validate: {
			newPassword: (value) => {
				if (value.length < 6) return "Password must be at least 6 characters long.";
				if (value.length > 30) return "Password must not exceed 30 characters.";
				return null;
			},
		},
	});

	useEffect(() => {
		const notificationId = Utils.displayNotification(
			"Getting user info...",
			undefined,
			"blue",
			"loading"
		);

		ConductionAPI.getUser(userId, token)
			.then((loadedUser: User | null) => {
				if (loadedUser === null) {
					setLoading(false)
					setSubjectUser(null);
					Utils.updateNotification(
						notificationId,
						"Could not load user data.",
						"You might not have access to this information.",
						"red",
						<IconAlertCircle size={16} />,
						true,
						false
					);
				} else {
					setLoading(false)
					setSubjectUser(loadedUser);
					Utils.updateNotification(
						notificationId,
						`Information Loaded for User: ${loadedUser.username}`,
						undefined,
						"green",
						<IconUser size={16} />
					);
				}
			})
			.catch(() => {
				Utils.updateNotification(
					notificationId,
					"Error Fetching User Data",
					"An error occurred while retrieving user data.",
					"red",
					<IconAlertCircle size={16} />,
					true,
					false
				);
			})
			.finally(() => setLoading(false));
	}, [])

	const handleSubmit = useCallback(async ({ newPassword }: typeof form.values) => {
		if (subjectUser === null) {
			return;
		}

		const notificationId = Utils.displayNotification(
			"Updating Password",
			"Processing password update...",
			"blue",
			"loading"
		);

		try {
			await ConductionAPI.updatePassword(subjectUser, newPassword, token);
			Utils.updateNotification(
				notificationId,
				`Password updated for user: ${subjectUser.username}!`,
				undefined,
				"green",
				<IconCheck size={16} />
			);
			onClose();
		} catch (error) {
			console.error("Error updating user information", error);
			Utils.updateNotification(
				notificationId,
				"Update Failed",
				"An error occurred while updating the password.",
				"red",
				<IconAlertCircle size={16} />,
				true,
				false
			);
		}
	}, [subjectUser]);

	const height = 20; // view height (vh)

	return (
		<ConductionModal
			title="Update Student Password"
			onClose={onClose}
			size="md"
		>
			<Box
				style={{
					minHeight: `${height}vh`
				}}
				onKeyDown={e => e.stopPropagation()} // Stop the key event from propagating to timeline where it is consumed as a hotkey
				onKeyUp={e => e.stopPropagation()}
			>
				{
					(loading) ? (
						<Skeleton height={"100%"} radius="md" />
					) : user === null ? (
						<Text>No users found.</Text>
					) : (
						<Flex
							align="stretch"
							justify="space-between"
							direction={"column"}
							style={{
								minHeight: `${height}vh`
							}}
						>
							<Text size="lg">
								Updating password for user: {(subjectUser as User).username}
							</Text>
							<Box>
								<Stack>
									<form onSubmit={form.onSubmit(handleSubmit)}>
										<Stack>
											<PasswordInput
												label={
													<Group>
														<IconLock size={20} />
														<Text>New Password</Text>
													</Group>
												}
												{...form.getInputProps("newPassword")}
												required
												withAsterisk={false}
											/>

											<Button
												type="submit"
												radius="xl"
												size='sm'
												loading={form.submitting}
												color="red"
											>
												Update Password
											</Button>
										</Stack>
									</form>
								</Stack>
							</Box>
						</Flex>
					)
				}
			</Box>
		</ConductionModal>
	);
};

export default StudentPasswordUpdateForm;
